
(function () {
  if (customElements.get('video-modal')) return;

  const HIDE_AFTER_MS = 300; // keep in sync with the CSS opacity transition

  class VideoModal extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-video-modal-dialog], .video-modal__dialog');
      this.stage = this.querySelector('[data-video-stage], .video-modal__stage');
      this.closeBtn = this.querySelector('[data-video-modal-close], .video-modal__close');

      this.video = null;
      this.lastFocus = null;
      this.scrollY = 0;
      this._hideTimer = 0;

      this.onCloseClick = () => this.close();
      this.onBackdropClick = (e) => { if (e.target === this) this.close(); };
      this.onKeydown = (e) => { if (e.key === 'Escape') this.close(); };

      this.closeBtn?.addEventListener('click', this.onCloseClick);
      this.addEventListener('click', this.onBackdropClick);

      this.hidden = true;
    }

    disconnectedCallback() {
      this.closeBtn?.removeEventListener('click', this.onCloseClick);
      this.removeEventListener('click', this.onBackdropClick);
      document.removeEventListener('keydown', this.onKeydown);
      clearTimeout(this._hideTimer);
      this.teardownVideo();
      if (this.isOpen) this.unlockScroll();
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    /**
     * @param {string} [url]       video source (defaults to data-video-url)
     * @param {HTMLVideoElement} [prepared]  an already-buffering <video> for the same url (a caller that
     *        warmed it up on hover/idle hands it over so playback starts without a network round trip)
     */
    open(url, prepared) {
      const src = url || this.dataset.videoUrl;
      if (!src || !this.stage) return;

      clearTimeout(this._hideTimer);
      this.lastFocus = document.activeElement;
      this.teardownVideo();

      const reuse = prepared && prepared.tagName === 'VIDEO' && prepared.getAttribute('src') === src;
      const video = reuse ? prepared : document.createElement('video');
      video.className = 'video-modal__video';
      if (!reuse) video.src = src;
      video.muted = false;
      video.controls = true;
      video.autoplay = true;
      video.playsInline = true;
      video.preload = 'auto';
      this.stage.appendChild(video);
      this.video = video;

      const played = video.play();
      if (played && typeof played.catch === 'function') played.catch(() => {});

      this.hidden = false;
      void this.offsetWidth; // force reflow so the opacity/scale transition runs
      this.classList.add('is-open');
      this.lockScroll();
      document.addEventListener('keydown', this.onKeydown);
      this.closeBtn?.focus({ preventScroll: true });
    }

    close() {
      if (!this.isOpen) return;

      this.classList.remove('is-open');
      this.unlockScroll();
      document.removeEventListener('keydown', this.onKeydown);
      this.teardownVideo(); // stop audio immediately, don't wait for the transition

      clearTimeout(this._hideTimer);
      this._hideTimer = setTimeout(() => { this.hidden = true; }, HIDE_AFTER_MS);

      if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
        this.lastFocus.focus({ preventScroll: true });
      }
    }

    teardownVideo() {
      const video = this.video;
      if (!video) return;
      try {
        video.pause();
        video.removeAttribute('src');
        video.load(); // releases the resource — kills audio right away
      } catch (e) {
        /* no-op */
      }
      video.remove();
      this.video = null;
    }

    lockScroll() {
      if (this._locked) return;
      this._locked = true;
      window.siloScrollLock.lock();
    }

    unlockScroll() {
      if (!this._locked) return;
      this._locked = false;
      window.siloScrollLock.unlock();
    }
  }

  customElements.define('video-modal', VideoModal);

  // One delegated trigger handler for the whole document (works for any number
  // of <video-modal>s, present now or injected later by section rendering).
  if (!window.__videoModalTriggerBound) {
    window.__videoModalTriggerBound = true;
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-video-modal-open]');
      if (!trigger) return;
      e.preventDefault();
      const id = trigger.getAttribute('data-video-modal-open');
      const modal = id ? document.getElementById(id) : trigger.closest('video-modal');
      if (modal && typeof modal.open === 'function') {
        modal.open(trigger.getAttribute('data-video-url'));
      }
    });
  }
})();
